<template>
  <div class="market-page">
    <NavBar />
    <main class="market-main" :aria-busy="loading">
      <aside class="market-sidebar">
        <div class="sidebar-title"><i class="bi bi-journal-bookmark" aria-hidden="true"></i>Eğitim kategorileri</div>
        <nav class="category-nav" aria-label="Eğitim kategorileri">
          <button type="button" :disabled="!categories.length" :class="{ selected: selectedCategoryId === null }" :aria-pressed="selectedCategoryId === null" @click="selectCategory(null)">Tüm eğitimler</button>
          <button v-for="category in categories" :key="category.id" type="button"
            :class="{ selected: selectedCategoryId === category.id }" :aria-pressed="selectedCategoryId === category.id"
            @click="selectCategory(category.id)">{{ category.categoryName }}</button>
        </nav>
      </aside>
      <div class="market-content">
      <header class="market-heading">
        <span class="market-eyebrow">EĞİTİMLERİ KEŞFET</span>
        <h1>Ders programlarını keşfet.</h1>
        <p>Kategorilere göz at, seviyene ve hedeflerine uygun eğitimi bul.</p>
      </header>
      <div class="catalog-heading">
        <h2>{{ selectedCategory ? selectedCategory.categoryName : 'Tüm eğitimler' }}</h2>
        <span v-if="!loading && !loadError" role="status">{{ categoryCards.length }} eğitim</span>
      </div>
      <div v-if="loading" class="market-state" role="status">
        <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
        <p>Eğitimler yükleniyor…</p>
      </div>
      <div v-else-if="loadError" class="market-state" role="alert">
        <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
        <h2>Eğitimler şu anda yüklenemedi.</h2>
        <p>Lütfen biraz sonra tekrar dene.</p>
        <button type="button" class="retry-button" @click="retryLoad">Tekrar dene</button>
      </div>
      <div v-else-if="!categoryCards.length" class="market-state">
        <i class="bi bi-book" aria-hidden="true"></i>
        <h2>Şu anda listelenecek eğitim bulunmuyor.</h2>
        <p>Eğitim seçeneklerini görmek için daha sonra tekrar uğra.</p>
      </div>
      <section v-else class="market-grid" aria-label="Eğitim seçenekleri">
        <router-link v-for="category in categoryCards" :key="category.id"
          :to="detailRoute(category.id)"
          class="education-card">
          <div class="education-image">
            <img v-if="category.image" :src="category.image" alt="" loading="lazy" decoding="async" width="960" height="480">
            <i v-else class="bi bi-book" aria-hidden="true"></i>
          </div>
          <div class="education-body">
              <div v-if="category.details.level.length" class="education-levels" aria-label="Eğitim seviyeleri">
                <span v-for="level in category.details.level" :key="level">{{ level }}</span>
              </div>
            <h3>{{ category.categoryName }}</h3>
            <p v-if="category.details.summaryDescription" class="education-description">{{ category.details.summaryDescription }}</p>
            <div class="education-action">
              <span class="details-button">Detaylar <i class="bi bi-arrow-right" aria-hidden="true"></i></span>
              <span v-if="category.details.lessonCount !== ''" class="lesson-count">
                {{ category.details.lessonCount }} ders
              </span>
            </div>
          </div>
        </router-link>
      </section>
      <div class="market-help">
        <router-link to="/faq"><i class="bi bi-question-circle" aria-hidden="true"></i>Aklına takılan bir şey mi var?<span>Sıkça sorulan sorular <i class="bi bi-arrow-up-right" aria-hidden="true"></i></span></router-link>
      </div>
      </div>
    </main>
    <FooterInPage />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import NavBar from '@/components/Shared/NavBar.vue';
import FooterInPage from '@/components/Shared/FooterInPage.vue';
import { GetCategoryProperty } from '@/common/market-helper';
import { getMarketImage } from '@/common/market-image';

export default {
  name: 'MarketPage',
  components: { NavBar, FooterInPage },
  data() {
    return { categories: [], visibleCategories: [], selectedCategoryId: null, loading: true, loadError: false, requestId: 0 };
  },
  computed: {
    CampaingCode() {
      return this.$route.query.campaingcode || '';
    },
    selectedCategory() {
      return this.categories.find(category => category.id === this.selectedCategoryId);
    },
    categoryCards() {
      return this.visibleCategories.map(category => {
        const details = GetCategoryProperty(category.properties);
        const image = getMarketImage(details);
        return { ...category, details, image };
      });
    }
  },
  methods: {
    ...mapActions(['GetCategories', 'AddMarketEvent']),
    detailRoute(id) {
      return { name: 'MarketDetail', params: { id }, query: { campaingcode: this.CampaingCode } };
    },
    retryLoad() {
      return this.categories.length ? this.selectCategory(this.selectedCategoryId) : this.getCategories();
    },
    async selectCategory(id) {
      const category = this.categories.find(item => item.id === id);
      if (category && category.hasChildCategory) {
        return this.$router.push(this.detailRoute(category.id));
      }
      const request = ++this.requestId;
      this.selectedCategoryId = id;
      this.loading = true;
      this.loadError = false;
      this.visibleCategories = [];
      try {
        const items = category ? [category] : this.categories;
        if (request === this.requestId) this.visibleCategories = items;
      } catch {
        if (request === this.requestId) this.loadError = true;
      } finally {
        if (request === this.requestId) this.loading = false;
      }
    },
    async getCategories() {
      const request = ++this.requestId;
      this.loading = true;
      this.loadError = false;
      try {
        const categories = await this.GetCategories(null);
        if (request !== this.requestId) return;
        if (!Array.isArray(categories)) throw new Error('Invalid category response');
        this.categories = categories;
        this.visibleCategories = categories;
        this.selectedCategoryId = null;
      } catch {
        if (request === this.requestId) this.loadError = true;
      } finally {
        if (request === this.requestId) this.loading = false;
      }
    }
  },
  mounted() {
    const description = this.CampaingCode ? 'Kampanya Kodu : ' + this.CampaingCode : '';
    this.AddMarketEvent({ event: 1, description }).catch(() => {});
    this.getCategories();
  },
  beforeUnmount() { ++this.requestId; }
};
</script>

<style scoped>
.market-page { min-height: 100vh; background: #fff; color: #10243a; }
.market-main { display: grid; grid-template-columns: 240px minmax(0, 1fr); max-width: 1680px; margin: 0 auto; padding: 0 32px; }
.market-sidebar { border-right: 1px solid #e5e9e8; padding: 48px 24px 32px 0; }
.sidebar-title { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; color: #637489; font-size: 13px; font-weight: 600; }
.sidebar-title i { color: var(--brand-link); font-size: 18px; }
.category-nav { display: flex; flex-direction: column; gap: 6px; position: sticky; top: 24px; }
.category-nav button { border: 0; border-radius: 8px; background: transparent; padding: 12px 14px; color: #34483b; text-align: left; font-size: 14px; font-weight: 600; line-height: 1.5; overflow-wrap: anywhere; }
.category-nav button:hover { background: #f5f8f6; }
.category-nav button.selected { background: #eaf4ee; color: var(--brand-link); }
.category-nav button:disabled { cursor: default; }
.market-content { min-width: 0; padding: 48px 0 32px 44px; }
.market-heading { margin-bottom: 44px; }
.catalog-heading { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 24px; }
.catalog-heading h2 { margin: 0; font-size: clamp(24px, 3vw, 34px); font-weight: 600; letter-spacing: -.7px; overflow-wrap: anywhere; }
.catalog-heading > span { color: #637489; font-size: 13px; white-space: nowrap; }
.market-eyebrow { color: var(--brand-link); font-size: 11px; font-weight: 700; letter-spacing: 1.5px; }
.market-heading h1 { margin: 12px 0; font-size: clamp(28px, 4vw, 40px); font-weight: 750; letter-spacing: -1.3px; line-height: 1.2; }
.market-heading p { margin: 0; color: #637489; font-size: 15px; line-height: 1.7; }
.market-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 24px; }
.education-card { display: flex; flex-direction: column; min-width: 0; overflow: hidden; border: 1px solid #e0e6ed; border-radius: 10px; background: #fff; color: inherit; text-decoration: none; transition: border-color .2s, box-shadow .2s, transform .2s; }
.education-card:hover { color: inherit; border-color: #a9c8bc; box-shadow: 0 12px 28px #10243a0a; transform: translateY(-3px); }
.education-image { aspect-ratio: 2 / 1; display: grid; place-items: center; overflow: hidden; background: #edf3f4; }
.education-image img { display: block; width: 100%; height: 100%; object-fit: contain; }
.education-image > i { font-size: 44px; color: #69998a; }
.education-body { display: flex; flex: 1; flex-direction: column; padding: 22px; }
.education-body h3 { font-size: 21px; font-weight: 600; letter-spacing: -.5px; line-height: 1.4; margin: 0 0 10px; overflow-wrap: anywhere; }
.education-description { font-size: 14px; color: #637489; line-height: 1.75; margin: 0 0 20px; }
.education-levels { display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 12px; }
.education-levels span { border-radius: 4px; background: #edf2ed; padding: 4px 7px; font-size: 10px; font-weight: 600; color: #4c6256; }
.lesson-count { display: inline-flex; align-items: center; gap: 7px; font-size: 12px; color: #637489; margin-left: auto; }
.education-action { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-top: auto; padding-top: 24px; font-size: 12px; color: var(--brand-link); }
.details-button { display: inline-flex; align-items: center; gap: 10px; border: 1px solid #b6c6bd; border-radius: 24px; padding: 7px 14px; }
.education-card:hover .details-button { background: #edf7f2; border-color: var(--brand-link); }
.market-state { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 14px; padding: 64px 24px; background: #fff; border: 1px solid #e0e6ed; border-radius: 20px; color: #637489; }
.market-state > i { font-size: 30px; color: var(--brand-link); }
.market-state h2 { margin: 0; font-size: 20px; color: #10243a; }
.market-state p { margin: 0; font-size: 14px; }
.retry-button { border: 0; border-radius: 10px; padding: 12px 20px; background: var(--brand-link); color: white; font-weight: 600; }
.market-help { margin-top: 32px; text-align: center; }
.market-help a { display: inline-flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 8px; padding: 8px 0; color: #637489; text-decoration: none; font-size: 13px; }
.market-help span { color: var(--brand-link); }
.market-help a:hover span { text-decoration: underline; }
.market-main a:focus-visible, .market-main button:focus-visible { outline: 3px solid var(--brand-link); outline-offset: 4px; }
@media (max-width: 1399px) {
  .market-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .market-main { grid-template-columns: 210px minmax(0, 1fr); padding: 0 24px; }
  .market-content { padding-left: 28px; }
}
@media (max-width: 1099px) {
  .market-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 767px) {
  .market-main { display: block; padding: 0 18px; }
  .market-sidebar { padding: 24px 0 18px; border-right: 0; border-bottom: 1px solid #e5e9e8; }
  .sidebar-title { margin-bottom: 12px; }
  .category-nav { position: static; flex-direction: row; overflow-x: auto; padding: 4px; scrollbar-width: thin; }
  .category-nav button { flex-shrink: 0; max-width: 260px; padding: 10px 14px; }
  .market-content { padding: 28px 0 24px; }
  .market-heading { margin-bottom: 28px; }
}
@media (max-width: 575px) {
  .market-heading { margin-bottom: 24px; }
  .market-grid { grid-template-columns: 1fr; gap: 18px; }
  .education-body { padding: 20px; }
  .education-body h3 { font-size: 21px; }
  .market-help a { font-size: 12px; }
}
@media (prefers-reduced-motion: reduce) {
  .education-card { transition: none; }
  .education-card:hover { transform: none; }
}
</style>
